// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { BrandingForm } from '@Organisms/Branding/BrandingForm/BrandingForm';
import client from '@Client';
import { BrandingParams, PermissionsEnum } from 'thehive-sdk';
import { openMessage, handleNotificationError } from '@Helpers/notification';
import { useGenericQuery } from '@Queries/genericQueries';
import { THLayout } from '@Molecules/THLayout';
import { computeEventList } from '@Organisms/Branding/eventList';
import { StatusContext } from '@Context/StatusProvider';
import { UnavailableMessage } from '@Molecules/UnavailableMessage/UnavailableMessage';
import { ICONS } from '@Constants/iconsApp';

import './BrandingDetails.scss';

const BrandingDetails = (): ReactElement => {
    const i = useLocalMessage();

    const { licenseCapabilities } = useContext(StatusContext);

    const brandingCapabilities = useMemo(() => !!licenseCapabilities.branding || false, [licenseCapabilities]);

    const { data, refetch } = useGenericQuery<BrandingParams>({
        retry: false,
        key: ['branding-props'],
        fetchCallback: useCallback(() => client.BrandingController.list(), []),
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.BrandingController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (msg?: string) => {
                    if (msg) {
                        openMessage('success', msg);
                    }
                    refetch();
                },
            }),
        [i, refetch],
    );

    const onHandlerBrandingFinish = (values: BrandingParams): void => {
        eventList.onEdit(values);
    };

    const onRemoveHandler = (kind: string) => {
        eventList.onDelete(kind);
    };

    return (
        <THLayout>
            {brandingCapabilities ? (
                <THLayout.Content>
                    <div className="branding-panel">
                        <BrandingForm onFinish={onHandlerBrandingFinish} onRemove={onRemoveHandler} data={data || {}} />
                    </div>
                </THLayout.Content>
            ) : (
                <UnavailableMessage type="capability" />
            )}
        </THLayout>
    );
};

const path = RouteBuilder.compute('administration.branding');

export const Route: RouteType = {
    path,
    menu: {
        label: 'account.branding.panel',
        icon: <ICONS.CopyrightIcon />,
    },
    breadcrumb: { label: 'breadcrumb.global.branding' },
    permissionNeeded: [PermissionsEnum.MANAGE_PLATFORM],
    content: <BrandingDetails />,
    space: ['admin'],
    exact: true,
};
